import { Checkbox, FormField, Input, NumberStepper, Select, Stack, useToast } from '@ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormDialog } from '../../../app/FormDialog';
import { formErrors } from '../../../lib/errors';
import { useSession } from '../../identity/session';
import {
  useArchiveFeedType,
  useArchiveTreatmentMethod,
  useFeedTypes,
  useSaveFeedType,
  useSaveTreatmentMethod,
  useTreatmentMethods,
  type FeedType,
  type FeedUnit,
  type TreatmentApplication,
  type TreatmentMethod,
} from '../api';
import { CatalogRow, CatalogSection, useHideToggle } from '../../harvest/components/CatalogSection';
import { HarvestCatalogs } from '../../harvest/components/HarvestCatalogs';
import { formatAmount } from '../format';
import styles from '../components/forms.module.css';

const APPLICATIONS: TreatmentApplication[] = [
  'drip',
  'spray',
  'evaporate',
  'sublimate',
  'strip',
  'biotechnical',
];

/**
 * Catalogs of treatment methods (TRT-002), feed types (FED-002), honey
 * varieties, containers and articles (BIZ-003).
 */
export function CatalogsPage() {
  const { can } = useSession();
  return (
    <Stack gap={6}>
      {can('treatments.read') && <MethodsCard />}
      {can('feedings.read') && <FeedTypesCard />}
      <HarvestCatalogs />
    </Stack>
  );
}

function MethodsCard() {
  const { t } = useTranslation();
  const { can } = useSession();
  const [hidden, setHidden] = useState(false);
  const [editing, setEditing] = useState<TreatmentMethod | 'new' | null>(null);
  const methods = useTreatmentMethods(hidden);
  const toggle = useHideToggle(useArchiveTreatmentMethod());
  const write = can('treatments.write');
  return (
    <CatalogSection
      title={t('apiary.catalogs.methods')}
      hidden={hidden}
      onHidden={setHidden}
      newLabel={t('apiary.catalogs.newMethod')}
      onNew={write ? () => setEditing('new') : undefined}
      loading={methods.isPending}
    >
      {(methods.data ?? []).map((m) => (
        <CatalogRow
          key={m.id}
          name={m.name}
          builtin={m.builtin}
          archived={m.archived}
          facts={[
            m.active_ingredient,
            m.application ? t(`apiary.application.${m.application}`) : null,
            m.default_amount != null
              ? `${formatAmount(m.default_amount, 3)} ${m.unit ?? ''}`
              : null,
            m.long_term ? t('apiary.treatment.longTerm') : null,
          ]}
          onEdit={write && !m.builtin ? () => setEditing(m) : undefined}
          onToggle={write ? () => toggle(m) : undefined}
        />
      ))}
      {editing && (
        <MethodDialog
          method={editing === 'new' ? undefined : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </CatalogSection>
  );
}

function MethodDialog({
  method,
  onClose,
}: {
  method?: TreatmentMethod | undefined;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const save = useSaveTreatmentMethod();
  const [name, setName] = useState(method?.name ?? '');
  const [ingredient, setIngredient] = useState(method?.active_ingredient ?? '');
  const [application, setApplication] = useState<string>(method?.application ?? '');
  const [amount, setAmount] = useState<number | null>(method?.default_amount ?? null);
  const [unit, setUnit] = useState(method?.unit ?? '');
  const [waiting, setWaiting] = useState<number | null>(method?.waiting_days ?? 0);
  const [longTerm, setLongTerm] = useState(method?.long_term ?? false);
  const errors = formErrors(save.error, { name: ['treatment_method.name'] });
  const labels = { decrement: t('common.decrease'), increment: t('common.increase') };
  return (
    <FormDialog
      open
      onClose={onClose}
      title={method ? method.name : t('apiary.catalogs.newMethod')}
      submitLabel={t('common.save')}
      pending={save.isPending}
      error={errors.general}
      onSubmit={() =>
        save.mutate(
          {
            id: method?.id,
            body: {
              name,
              active_ingredient: ingredient || null,
              application: (application || null) as TreatmentApplication | null,
              default_amount: amount,
              unit: unit || null,
              waiting_days: waiting ?? 0,
              long_term: longTerm,
            },
          },
          {
            onSuccess: () => {
              toast.show(t('apiary.catalogs.saved'));
              onClose();
            },
          },
        )
      }
    >
      <FormField label={t('apiary.catalogs.name')} required error={errors.field.name}>
        {(p) => (
          <Input {...p} value={name} maxLength={100} onChange={(e) => setName(e.target.value)} />
        )}
      </FormField>
      <div className={styles.row}>
        <FormField label={t('apiary.catalogs.activeIngredient')}>
          {(p) => (
            <Input {...p} value={ingredient} onChange={(e) => setIngredient(e.target.value)} />
          )}
        </FormField>
        <FormField label={t('apiary.treatment.application')}>
          {(p) => (
            <Select
              {...p}
              value={application}
              onChange={(e) => setApplication(e.target.value)}
              options={[
                { value: '', label: t('apiary.treatment.noApplication') },
                ...APPLICATIONS.map((a) => ({ value: a, label: t(`apiary.application.${a}`) })),
              ]}
            />
          )}
        </FormField>
      </div>
      <div className={styles.row}>
        <FormField label={t('apiary.catalogs.defaultAmount')}>
          {(p) => (
            <NumberStepper
              {...p}
              value={amount}
              onChange={setAmount}
              min={0}
              decimals={3}
              labels={labels}
            />
          )}
        </FormField>
        <FormField label={t('apiary.treatment.unit')}>
          {(p) => (
            <Input {...p} value={unit} maxLength={20} onChange={(e) => setUnit(e.target.value)} />
          )}
        </FormField>
        <FormField label={t('apiary.treatment.waitingDays')}>
          {(p) => (
            <NumberStepper
              {...p}
              value={waiting}
              onChange={setWaiting}
              min={0}
              max={365}
              labels={labels}
            />
          )}
        </FormField>
      </div>
      <Checkbox
        label={t('apiary.treatment.longTerm')}
        description={t('apiary.treatment.longTermHint')}
        checked={longTerm}
        onChange={(e) => setLongTerm(e.target.checked)}
      />
    </FormDialog>
  );
}

function FeedTypesCard() {
  const { t } = useTranslation();
  const { can } = useSession();
  const [hidden, setHidden] = useState(false);
  const [editing, setEditing] = useState<FeedType | 'new' | null>(null);
  const types = useFeedTypes(hidden);
  const toggle = useHideToggle(useArchiveFeedType());
  const write = can('feedings.write');
  return (
    <CatalogSection
      title={t('apiary.catalogs.feedTypes')}
      hidden={hidden}
      onHidden={setHidden}
      newLabel={t('apiary.catalogs.newFeedType')}
      onNew={write ? () => setEditing('new') : undefined}
      loading={types.isPending}
    >
      {(types.data ?? []).map((f) => (
        <CatalogRow
          key={f.id}
          name={f.name}
          builtin={f.builtin}
          archived={f.archived}
          facts={[
            f.unit,
            f.sugar_per_kg != null ? `${f.sugar_per_kg} ‰` : null,
            f.sugar_per_l != null ? `${f.sugar_per_l} g/l` : null,
          ]}
          onEdit={write && !f.builtin ? () => setEditing(f) : undefined}
          onToggle={write ? () => toggle(f) : undefined}
        />
      ))}
      {editing && (
        <FeedTypeDialog
          feedType={editing === 'new' ? undefined : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </CatalogSection>
  );
}

function FeedTypeDialog({
  feedType,
  onClose,
}: {
  feedType?: FeedType | undefined;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const save = useSaveFeedType();
  const [name, setName] = useState(feedType?.name ?? '');
  const [unit, setUnit] = useState<FeedUnit>(feedType?.unit ?? 'kg');
  const [perKg, setPerKg] = useState<number | null>(feedType?.sugar_per_kg ?? null);
  const [perL, setPerL] = useState<number | null>(feedType?.sugar_per_l ?? null);
  const errors = formErrors(save.error, {
    name: ['feed_type.name'],
    perKg: ['feed_type.sugar_per_kg'],
    perL: ['feed_type.sugar_per_l'],
  });
  const labels = { decrement: t('common.decrease'), increment: t('common.increase') };
  return (
    <FormDialog
      open
      onClose={onClose}
      title={feedType ? feedType.name : t('apiary.catalogs.newFeedType')}
      submitLabel={t('common.save')}
      pending={save.isPending}
      error={errors.general}
      onSubmit={() =>
        save.mutate(
          { id: feedType?.id, body: { name, unit, sugar_per_kg: perKg, sugar_per_l: perL } },
          {
            onSuccess: () => {
              toast.show(t('apiary.catalogs.saved'));
              onClose();
            },
          },
        )
      }
    >
      <FormField label={t('apiary.catalogs.name')} required error={errors.field.name}>
        {(p) => (
          <Input {...p} value={name} maxLength={100} onChange={(e) => setName(e.target.value)} />
        )}
      </FormField>
      <FormField label={t('apiary.feeding.unit')}>
        {(p) => (
          <Select
            {...p}
            value={unit}
            onChange={(e) => setUnit(e.target.value as FeedUnit)}
            options={[
              { value: 'kg', label: t('apiary.unit.kg') },
              { value: 'l', label: t('apiary.unit.l') },
            ]}
          />
        )}
      </FormField>
      <div className={styles.row}>
        <FormField label={t('apiary.catalogs.sugarPerKg')} error={errors.field.perKg}>
          {(p) => (
            <NumberStepper
              {...p}
              value={perKg}
              onChange={setPerKg}
              min={0}
              max={2000}
              step={10}
              labels={labels}
            />
          )}
        </FormField>
        <FormField label={t('apiary.catalogs.sugarPerL')} error={errors.field.perL}>
          {(p) => (
            <NumberStepper
              {...p}
              value={perL}
              onChange={setPerL}
              min={0}
              max={2000}
              step={10}
              labels={labels}
            />
          )}
        </FormField>
      </div>
    </FormDialog>
  );
}
